import { FullPageSpinner, ProtectedRoute, SuspenseRoute } from "./Components";
import { Mixpanel } from "./utils/mixpanelWrapper";
import { Router, navigate, Redirect } from "@reach/router";
import { useCompanyInfo, SetCompany } from "./redux/company";
import { useExperimentFlag } from "./utils/experiments/experiment-utils";
import { useSelector, useDispatch } from "react-redux";
import * as R from "ramda";
import * as UserRedux from "./redux/user";
import CreativeMapV2 from "./CreativeMapV2/CreativeMap";
import AddNewClient from "./AddNewClient";
import BrandEquity from "./BrandEquity";
import BrandHealthMetrics from "./BrandHealthMetrics";
import BudgetIntakeTool from "./BudgetIntakeTool";
import ClientInfoForm from "./Onboarding";
import CommerceTool from "./Commerce";
import CompetitiveMetrics from "./CompetitiveMetrics";
import CompetitiveSpots from "./CompetitiveSpots";
import Copytiger from "./Copytiger";
import CreativeInsights from "./CreativeInsights";
import CreativeMap from "./CreativeMap";
import CreativeView from "./CreativeView";
import CroOnboard from "./CroOnboard";
import CrossChannel from "./CrossChannel";
import CustomerInsights from "./CustomerInsights";
import CustomTableau from "./CustomTableau";
import Dashboard from "./Dashboard";
import DataFreshness from "./DataFreshness";
import ExpectedBookings from "./ExpectedBookings";
import FiscalCalendarInputPage from "./FiscalCalendarInput";
import ForecastingPage from "./Forecasting";
import GeoMMM from "./GeoMMM";
import SotGa4MappingTool from "./SotGa4MappingTool";
import HomePage from "./HomePage";
import Insights from "./AMC/Insights";
import KpiMapping from "./KpiMapping";
import LinearBuying from "./LinearBuying";
import LinearDelivery from "./LinearDelivery";
import LinearMLDiagnostics from "./LinearMLDiagnostics";
import LinearOptimizations from "./LinearOptimizations";
import LinearOrders from "./LinearOrders";
import LinearPerformance from "./Performance/LinearPerformance";
import LinearPlans from "./LinearPlans";
import LinearSingleChannel from "./SingleChannel/Channels/LinearSingleChannel";
import LinearUpfrontReconciliation from "./LinearUpfrontReconciliation";
import MetaBuying from "./MetaBuying";
import MMM from "./MMM";
import MobiusView from "./MobiusView";
import NotFound from "./NotFound";
import Pacing from "./Pacing";
import Properties from "./Properties";
import React, { useMemo, useEffect } from "react";
import ReferenceView from "./ReferenceView";
import Reporting from "./Reporting";
import RetailOpsChannels from "./RetailOpsChannels";
import SegmentationMapping from "./SegmentationMapping/SegmentationMapping";
import Slides from "./Slides";
import SpikeChart from "./SpikeChart";
import StreamingBuying from "./StreamingBuying";
import StreamingCreatives from "./StreamingCreatives";
import StreamingDelivery from "./StreamingDelivery";
import StreamingExperiments from "./StreamingExperiments";
import StreamingGeoResponse from "./StreamingGeoResponse";
import StreamingGeoTargeting from "./StreamingGeoTargeting";
import StreamingInvoicing from "./StreamingInvoicing";
import StreamingMLDiagnostics from "./StreamingMLDiagnostics";
import StreamingOptimizations from "./StreamingOptimizations";
import StreamingOrderStatus from "./StreamingOrderStatus";
import StreamingPerformance from "./Performance/StreamingPerformance";
import StreamingPlansBuilder from "./StreamingPlansBuilder";
import StreamingReports from "./StreamingReports/StreamingReports";
import StreamingSingleChannel from "./SingleChannel/Channels/StreamingSingleChannel";
import StreamingWizard from "./StreamingWizard";
import TVADCrossChannel from "./TVADCrossChannel";
import useLocation from "./utils/hooks/useLocation";
import WebTrafficOptimization from "./WebTrafficOptimization";
import YouTubeDelivery from "./YouTubeDelivery/YouTubeDelivery";
import YouTubePerformance from "./Performance/YouTubePerformance";
import YouTubePlanning from "./YouTubePlanning";
import YouTubeSingleChannel from "./SingleChannel/Channels/YouTubeSingleChannel";
import SignalDashboard from "./SignalDashboard";
import { SingleChannelFactory } from "./SingleChannel/Channels/SingleChannelFactory";
import {
  COMMERCE,
  PROGRAMMATIC,
  SOCIAL,
  SEARCHSHOPPING,
  EMAIL,
} from "@blisspointmedia/bpm-types/dist/ScpChannels";

const CompanyRoutes = ({ company }) => {
  const { location } = useLocation();
  const dispatch = useDispatch();
  const companies = useSelector(UserRedux.companiesSelector);
  const companyInfo = useCompanyInfo();

  const shouldEnableAudioGraphPerformance = !useExperimentFlag("showIPAudioPerformance");
  const shouldEnableBrandHealthMetrics =
    useExperimentFlag("enableBrandHealthMetrics") || company === "tinuititest";
  const shouldEnableBrandHealthGoogle = useExperimentFlag("enableBrandHealthGoogle");
  const shouldEnableCreativeInsightsExperiment = useExperimentFlag("enableCreativeInsights");
  const shouldEnableCustomerInsightsExperiment = useExperimentFlag("enableCustomerInsights");
  const shouldEnableDisplayGraphPerformance = !useExperimentFlag("showIPDisplayPerformance");
  const shouldEnableGraphPerformance = !useExperimentFlag("showIPStreamingPerformance");
  const shouldEnableMobiusIntegration = useExperimentFlag("enableMobiusIntegration");
  const shouldEnableMobiusSlidesCompany = useExperimentFlag("enableMobiusSlidesCompany");
  const shouldEnableStreamingGeoTargetingExperiment = useExperimentFlag(
    "enableStreamingGeoTargeting"
  );
  const shouldNotDefaultHomeV2 = useExperimentFlag("notDefaultHomeV2");
  const shouldEnableMetaBuyingExperiment = useExperimentFlag("enableMetaBuying");
  const shouldEnableGeoMMM = useExperimentFlag("enableGeoMMM") || company === "tinuititest";
  const shouldEnableRadioBuying = useExperimentFlag("enableRadioBuying");
  const shouldEnableForecasting = useExperimentFlag("enableForecasting");
  const shouldEnableMetaSignalDashboard = useExperimentFlag("enableMetaSignalDashboard");
  const shouldEnableCopytigerPage = useExperimentFlag("enableCopytiger");
  const shouldEnableEmailScp = useExperimentFlag("enableEmailScp");
  const isValidCompany = useMemo(() => R.any(elem => elem.company === company, companies), [
    companies,
    company,
  ]);

  useEffect(() => {
    // TODO we need to set this to null for non company routes
    Mixpanel.register({ company: company });
  }, [company]);

  useEffect(() => {
    if (company !== company.toLowerCase()) {
      R.pipe(R.prop("pathname"), R.replace(company, company.toLowerCase()), navigate)(location);
      return;
    }
    if (company && isValidCompany && (!companyInfo.cid || company !== companyInfo.cid)) {
      dispatch(SetCompany(company));
    }
  }, [company, isValidCompany, companyInfo, location, dispatch]);

  if (companies && !isValidCompany) {
    return <NotFound invalidCompany={company} />;
  } else if (!companyInfo.cid) {
    return <FullPageSpinner path="*" />;
  } else {
    return (
      <Router className="bpmRouter">
        <ProtectedRoute
          component={RetailOpsChannels}
          key="retail ops channels"
          path="/retail-ops-channels"
        />
        {/* Home */}
        {shouldNotDefaultHomeV2 ? (
          <>
            <Redirect noThrow from="/" to="home" />
            <HomePage key="home" path="/home" />
          </>
        ) : (
          <>
            <Redirect noThrow from="/" to="cross-channel" />
            <HomePage key="home" path="/home-archived" />
          </>
        )}
        <ProtectedRoute key="onboarding" path="/client-info/*" component={ClientInfoForm} />
        <TVADCrossChannel key="tvadcrosschannel" path="/tvad-cross-channel" />
        <CrossChannel key="crosschannel" path="/cross-channel/*urlSettings" />
        <CrossChannel key="crosschannel" path="/cross_channel/*urlSettings" />
        <CrossChannel key="crosschannel" path="/crosschannel/*urlSettings" />

        {/* Metrics */}
        <Dashboard key="dashboard" path="/dashboard" />
        <CreativeView key="creative view" path="/creatives" />

        {/* Creatives */}
        <ProtectedRoute
          component={CreativeMapV2}
          key="creative_map_v2"
          path="/creative-map-v2/*"
          roleRequired={0}
        />

        {shouldEnableCreativeInsightsExperiment && (
          <CreativeInsights key="creative insights" path="/creative-insights" />
        )}
        <Redirect noThrow from="/creative_insights" to="../creative-insights" />
        <Redirect noThrow from="/creativeinsights" to="../creative-insights" />

        {shouldEnableCustomerInsightsExperiment && (
          <CustomerInsights key="customer insights" path="/customer-insights/*" />
        )}
        <Redirect noThrow from="/customer_insights" to="../customer-insights" />
        <Redirect noThrow from="/customerinsights" to="../customer-insights" />

        {(shouldEnableBrandHealthMetrics || shouldEnableBrandHealthGoogle) && (
          <BrandHealthMetrics key="brand health metrics" path="brand-health-metrics/*" />
        )}
        <Redirect noThrow from="/brand_health_metrics" to="../brand-health-metrics" />
        <ProtectedRoute component={DataFreshness} key="data freshness" path="/data-freshness" />
        <ProtectedRoute
          key="segmentation-mapping"
          path="/segmentation-mapping/*"
          component={SegmentationMapping}
          roleRequired={2}
        />
        <ProtectedRoute
          key="sot-ga4-mapping-tool"
          path="/sot-ga4-mapping-tool/*"
          component={SotGa4MappingTool}
        />
        <ReferenceView key="reference view" path="/reference" />
        {/* Linear */}
        <LinearPerformance
          key="linear performance"
          path={"/linear/performance/*urlPresetName"}
          prefix="tv"
        />
        <SuspenseRoute
          key="linear delivery"
          path="/linear/delivery"
          component={LinearDelivery}
          prefix="linear"
        />
        <SuspenseRoute key="spike" path="/linear/spike" component={SpikeChart} />
        <ProtectedRoute key="linear buying" path="/linear/buying" component={LinearBuying} />
        <ProtectedRoute key="linear plans" path="/linear/plans" component={LinearPlans} />
        <ProtectedRoute key="linear orders" path="/linear/orders" component={LinearOrders} />
        {/* Radio */}
        {shouldEnableRadioBuying && (
          <ProtectedRoute
            key="radio buying"
            path="/radio/buying"
            component={LinearBuying}
            channel="radio"
          />
        )}
        {/* Streaming */}
        <StreamingPerformance
          key="streaming performance"
          path={"/streaming/performance/*urlPresetName"}
          prefix="streaming"
          isGraph={shouldEnableGraphPerformance}
        />
        <StreamingPerformance
          key="streaming performance"
          path={"/streaming/performance_graph/*urlPresetName"}
          prefix="streaming"
          isGraph={true}
        />
        <StreamingPerformance
          key="streaming performance"
          path={"/streaming/performance_tu/*urlPresetName"}
          prefix="streaming"
          isGraph={true}
          isTransunionGraph={true}
        />
        <StreamingPerformance
          key="streaming performance"
          path={"/streaming/performance_ip/*urlPresetName"}
          prefix="streaming"
        />
        <SuspenseRoute
          key="streaming delivery"
          path="/streaming/delivery"
          component={StreamingDelivery}
          prefix="streaming"
          isGraph={shouldEnableGraphPerformance}
        />
        {/* New Single Channel Routes */}
        <ProtectedRoute
          component={StreamingSingleChannel}
          isGraph={shouldEnableGraphPerformance}
          key="streaming single channel"
          path={"/streaming/*urlSettings"}
          prefix="streaming"
          roleRequired={10}
        />
        <ProtectedRoute
          component={StreamingSingleChannel}
          isGraph={shouldEnableAudioGraphPerformance}
          key="audio single channel"
          path={"/audio/*urlSettings"}
          prefix="audio"
          roleRequired={10}
        />
        <ProtectedRoute
          component={StreamingSingleChannel}
          isGraph={shouldEnableDisplayGraphPerformance}
          key="display single channel"
          path={"/display/*urlSettings"}
          prefix="display"
          roleRequired={10}
        />
        <ProtectedRoute
          component={LinearSingleChannel}
          key="linear single channel"
          path={"/linear/*urlSettings"}
          roleRequired={10}
        />
        <ProtectedRoute
          component={LinearUpfrontReconciliation}
          key="linear upfront reconciliation"
          path={"/linear/upfront/"}
          roleRequired={10}
        />
        <ProtectedRoute
          component={YouTubeSingleChannel}
          key="youtube single channel"
          path={"/youtube/*urlSettings"}
          roleRequired={10}
        />
        <ProtectedRoute
          component={SingleChannelFactory}
          key="social single channel"
          path={"/social/*urlSettings"}
          roleRequired={10}
          channel={SOCIAL}
        />
        <ProtectedRoute
          component={SingleChannelFactory}
          key="search shopping single channel"
          path={"/search-shopping/*urlSettings"}
          roleRequired={10}
          channel={SEARCHSHOPPING}
        />
        <ProtectedRoute
          component={SingleChannelFactory}
          key="programmatic single channel"
          path={"/programmatic/*urlSettings"}
          roleRequired={10}
          channel={PROGRAMMATIC}
        />
        {shouldEnableEmailScp && (
          <ProtectedRoute
            component={SingleChannelFactory}
            key="email single channel"
            path={"/email/*urlSettings"}
            roleRequired={1}
            channel={EMAIL}
          />
        )}
        {shouldEnableMetaBuyingExperiment && (
          <ProtectedRoute
            key="meta-buying"
            path="/social/meta-buying/*"
            suspense={true}
            component={MetaBuying}
          />
        )}
        {shouldEnableMetaSignalDashboard && (
          <ProtectedRoute
            key="meta-signal-dashboard"
            path="/social/meta-signal-dashboard"
            suspense={true}
            component={SignalDashboard}
          />
        )}
        <Redirect noThrow from="/meta_signal_dashboard" to="../meta-signal-dashboard" />
        <Redirect noThrow from="/metasignaldashboard" to="../meta-signal-dashboard" />
        <ProtectedRoute
          component={SingleChannelFactory}
          key="commerce single channel"
          path={"/commerce/*urlSettings"}
          roleRequired={10}
          channel={COMMERCE}
        />
        {/* Youtube */}
        <ProtectedRoute
          key="youtube planning"
          path="/youtube/planning"
          component={YouTubePlanning}
        />
        <SuspenseRoute
          key="youtube performance"
          path="/youtube/performance/*urlPresetName"
          component={YouTubePerformance}
        />
        <SuspenseRoute
          key="youtube delivery"
          path="/youtube/delivery/"
          component={YouTubeDelivery}
        />
        <StreamingGeoResponse
          key="streaming geo response"
          path="/streaming/geo-response"
          prefix="streaming"
        />
        <Redirect noThrow from="/streaming/geo_response" to="../streaming/geo-reponse" />
        <StreamingGeoResponse key="audio geo response" path="/audio/geo-response" prefix="audio" />
        <Redirect noThrow from="/audio/geo_response" to="../audio/geo-reponse" />
        {shouldEnableStreamingGeoTargetingExperiment && (
          <>
            <ProtectedRoute
              key="streaming geo targeting"
              path="/streaming/geo_targeting/*orderInfo"
              component={StreamingGeoTargeting}
              prefix="streaming"
            />
            <ProtectedRoute
              key="streaming geo targeting"
              path="/streaming/geo-targeting/*orderInfo"
              component={StreamingGeoTargeting}
              prefix="streaming"
            />
          </>
        )}
        <ProtectedRoute
          key="streaming creatives"
          path="/streaming/creatives/*"
          component={StreamingCreatives}
        />
        <ProtectedRoute
          suspense
          key="streaming wizard"
          path="/streaming/wizard/*"
          component={StreamingWizard}
        />
        <ProtectedRoute
          key="streaming buying"
          path="/streaming/buying/*"
          component={StreamingBuying}
        />
        <ProtectedRoute
          key="streaming reports"
          path="/streaming/reports/*"
          component={StreamingReports}
        />
        <ProtectedRoute
          key="streaming status"
          path="/streaming/status/*"
          component={StreamingOrderStatus}
        />
        <ProtectedRoute
          key="streaming invoicing"
          path="/streaming/invoicing"
          component={StreamingInvoicing}
        />
        <ProtectedRoute
          key="streamingPlansBuilder"
          path="/streaming/plan-builder"
          component={StreamingPlansBuilder}
        />
        <Redirect noThrow from="/streaming/planbuilder" to="../streaming/plan-builder" />
        <StreamingExperiments key="streamingExperiments" path="/streaming/experiments" />
        {/* Audio */}
        <StreamingPerformance
          key="audio performance"
          path={"/audio/performance/*urlPresetName"}
          prefix="audio"
          isGraph={shouldEnableAudioGraphPerformance}
        />
        <StreamingPerformance
          key="audio performance"
          path={"/audio/performance_graph/*urlPresetName"}
          prefix="audio"
          isGraph={true}
        />
        <StreamingPerformance
          key="audio performance"
          path={"/audio/performance_tu/*urlPresetName"}
          prefix="audio"
          isGraph={true}
          isTransunionGraph={true}
        />
        <StreamingPerformance
          key="audio performance"
          path={"/audio/performance_ip/*urlPresetName"}
          prefix="audio"
        />
        <SuspenseRoute
          key="audio delivery"
          path="/audio/delivery"
          component={StreamingDelivery}
          prefix="audio"
          isGraph={shouldEnableAudioGraphPerformance}
        />
        {/* Display */}
        <StreamingPerformance
          key="display performance"
          path={"/display/performance/*urlPresetName"}
          prefix="display"
          isGraph={shouldEnableDisplayGraphPerformance}
        />
        <StreamingPerformance
          key="display performance"
          path={"/display/performance_graph/*urlPresetName"}
          prefix="display"
          isGraph={true}
        />
        <StreamingPerformance
          key="display performance"
          path={"/display/performance_tu/*urlPresetName"}
          prefix="display"
          isGraph={true}
          isTransunionGraph={true}
        />
        <StreamingPerformance
          key="display performance"
          path={"/display/performance_ip/*urlPresetName"}
          prefix="display"
        />
        <SuspenseRoute
          key="display delivery"
          path="/display/delivery"
          component={StreamingDelivery}
          prefix="display"
          isGraph={shouldEnableDisplayGraphPerformance}
        />
        <ProtectedRoute key="expectedbookings" path="/bookings" component={ExpectedBookings} />
        {/* Mobius */}
        {shouldEnableMobiusIntegration && <MobiusView key="mobius" path="/mobius/*" />}
        {shouldEnableMobiusSlidesCompany && (
          <ProtectedRoute
            suspense
            key="mobiusslides"
            path="/mobius-slides"
            component={Slides}
            roleRequired={2}
          />
        )}
        <Redirect noThrow from="/mobius_slides" to="../mobius-slides" />
        <ProtectedRoute
          component={Insights}
          key="amc insights"
          path="/amc-insights/*"
          roleRequired={company === "dripdrop" ? 2 : 10}
        />
        <CommerceTool key="commerce" path="/commerce-tools" />
        {/* Competitive */}
        <CompetitiveMetrics key="competitive metrics" path="/competitive/metrics" />
        <CompetitiveSpots key="competitive spots" path="/competitive/spots" />

        {/* Channels? */}
        <Redirect noThrow from="/mmm/*" to="../rmmm/" />
        <MMM key="rmmm" path="/rmmm/*" />
        <MMM key="mmm" path="/mmm/*" />
        {shouldEnableGeoMMM && (
          <ProtectedRoute key="geo mmm" path="/geo-mmm/*" component={GeoMMM} roleRequired={10} />
        )}
        <ProtectedRoute
          key="custom tableau"
          path="/custom/*dashboardName"
          component={CustomTableau}
        />
        {/* Admin */}
        <ProtectedRoute key="creativemap" path="/creative-map/*" component={CreativeMap} />
        <ProtectedRoute key="creativemap" path="/creativemap/*" component={CreativeMap} />
        <ProtectedRoute
          key="kpiMapping"
          path="/kpi-mapping"
          component={KpiMapping}
          roleRequired={3}
        />
        <ProtectedRoute
          key="fiscalCalendarInput"
          path="/calendar-input"
          component={FiscalCalendarInputPage}
        />
        <Redirect noThrow from="/powerpoint" to="../slides" />
        <ProtectedRoute suspense key="slides" path="/slides" component={Slides} />
        <ProtectedRoute
          key="linear_optimizations"
          path="/linear/optimizations/*"
          component={LinearOptimizations}
        />
        <ProtectedRoute
          key="streaming_optimizations"
          path="/streaming/optimizations/*"
          component={StreamingOptimizations}
        />
        <ProtectedRoute
          key="audio_optimizations"
          path="/audio/optimizations/*"
          component={StreamingOptimizations}
        />
        <ProtectedRoute key="reporting" path="/reporting" component={Reporting} />
        {/* Properties */}
        <ProtectedRoute key="properties" path="/properties" component={Properties} />
        <ProtectedRoute
          key="diagnostics"
          path="/linear/diagnostics/*"
          component={LinearMLDiagnostics}
        />
        <ProtectedRoute
          key="diagnostics"
          path="/streaming/diagnostics/*"
          component={StreamingMLDiagnostics}
        />
        <ProtectedRoute
          key="budgetintaketool"
          path="budget-intake-tool/*"
          component={BudgetIntakeTool}
        />
        <Redirect noThrow from="/budgetintaketool" to="../budget-intake-tool" />
        <Redirect noThrow from="/budget_intake_tool" to="../budget-intake-tool" />
        <Redirect noThrow from="/budget-intake" to="../budget-intake-tool" />
        <Redirect noThrow from="/budgetintake" to="../budget-intake-tool" />
        <Redirect noThrow from="/budget_intake" to="../budget-intake-tool" />
        <ProtectedRoute key="pacing" path="pacing/*" component={Pacing} />
        <ProtectedRoute key="croonboard" path="/cro-onboard" component={CroOnboard} />
        <ProtectedRoute key="wto" path="/wto" component={WebTrafficOptimization} />
        <ProtectedRoute
          key="client tools"
          path="/edit-client"
          component={AddNewClient}
          existingClient={true}
          roleRequired={1}
        />
        <Redirect noThrow from="/edit_client" to="../edit-client" />
        <ProtectedRoute
          key="brandequity"
          path="brand-equity/*"
          component={BrandEquity}
          roleRequired={10}
        />
        <Redirect noThrow from="/brand_equity" to="../brand-equity" />
        <Redirect noThrow from="/brandequity" to="../brand-equity" />
        <ProtectedRoute
          key="brandequityamazon"
          path="brand-equity-amazon/*"
          component={BrandEquity}
          roleRequired={2}
        />
        {shouldEnableForecasting && (
          <ProtectedRoute key="forecasting" path="/forecasting/*" component={ForecastingPage} />
        )}
        {shouldEnableCopytigerPage && (
          <ProtectedRoute key="copytiger" path="copytiger/*" component={Copytiger} />
        )}
        <NotFound key="404" default />
      </Router>
    );
  }
};

export default CompanyRoutes;
